import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { Avatar } from '@/shared/ui/Avatar'
import { Trash2, Star, Calendar, MessageSquareText, ArrowRight } from 'lucide-react'
import { cn, formatDate } from '@/shared/utils'
import { useTrainerReviews, useStudentReviews } from '@/api/hooks'
import { usePagination } from '@/shared/hooks'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { toast } from '@/store/useToastStore'
import { Can } from '@/shared/ui/Can'

const TABS = [
  { labelKey:'tabTrainerReviews', path:'/dashboard/review/trainer' },
  { labelKey:'tabStudentReviews', path:'/dashboard/review/student' },
]

function Stars({ n, size = 13 }: { n: number; size?: number }) {
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({length:5},(_,i)=>(
        <Star key={i} size={size} className={i < Math.round(n) ? 'text-brand-400 fill-brand-400' : 'text-gray-200 fill-gray-200'}/>
      ))}
    </span>
  )
}

export function ReviewPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const isTrainer = location.pathname.includes('trainer')
  const isSchool = location.pathname.startsWith('/school')
  const tabs = isSchool
    ? TABS.map(tb => ({ ...tb, path: tb.path.replace('/dashboard/', '/school/') }))
    : TABS
  const { pagination } = usePagination()

  const params = { page:pagination.page, limit:pagination.per_page, per_page:pagination.per_page }
  const trainerQ = useTrainerReviews(isTrainer ? params : undefined)
  const studentQ = useStudentReviews(!isTrainer ? params : undefined)
  const q = isTrainer ? trainerQ : studentQ

  const [removingId, setRemovingId] = useState<number | null>(null)
  const confirm = useConfirm()

  const handleRemove = async (sessionId: number) => {
    if (!sessionId) return
    const ok = await confirm({ title: t('common.confirmDeleteTitle'), description: t('reviewPage.confirmRemove'), confirmLabel: t('common.delete') })
    if (!ok) return
    setRemovingId(sessionId)
    try {
      const delEp = isSchool ? endpoints.reviewSchoolAdmin.deleteReview : endpoints.review.deleteReview
      await restClient.post(`${delEp}delete_student_comment=1&session_id=${sessionId}`, {})
      toast.success(t('reviewPage.deleteSuccess'))
      await q.refetch?.()
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('reviewPage.deleteError'))
    } finally {
      setRemovingId(null)
    }
  }

  // API returns groups — one per trainer (Trainer Reviews tab) or per student (Student
  // Reviews tab) — each with its own reviews[] array. Keep that grouping instead of
  // flattening, so all of one trainer's/student's reviews sit together under their name.
  const groups: any[] = (q.data as any)?.data ?? []
  const groupedReviews = groups
    .map((g: any) => ({
      key:       g.trainer_id ?? g.student_id ?? g.id,
      name:      (isTrainer ? g.trainer_name : g.student_name) ?? '—',
      avgRating: Number(g.avg_rating ?? 0),
      count:     g.rating_count ?? (g.reviews?.length ?? 0),
      reviews: (g.reviews ?? []).map((rv: any) => ({
        id:        rv.session_id ?? rv.booking_id,
        sessionId: rv.session_id,
        reviewer:  (isTrainer ? (rv.student_name ?? g.student_name) : (rv.trainer_name ?? g.trainer_name)) ?? '—',
        rating:    Number(rv.user_rating ?? rv.driver_rating ?? rv.rating ?? g.avg_rating ?? 0),
        comment:   rv.user_comments ?? rv.driver_comments ?? rv.comment ?? '',
        date:      rv.uploaded_at ?? rv.created_at,
      })),
    }))
    .filter(g => g.reviews.length > 0)

  const totalReviews = groupedReviews.reduce((sum, g) => sum + g.reviews.length, 0)

  // Flatten to one row per review, tagging both sides so the table can show
  // plainly which name is the trainer and which is the student. On the Trainer
  // tab the group is the trainer and the reviewer is the student; on the Student
  // tab it's the other way round.
  const rows = groupedReviews.flatMap(g =>
    g.reviews.map((r: any) => ({
      ...r,
      trainerName: isTrainer ? g.name : r.reviewer,
      studentName: isTrainer ? r.reviewer : g.name,
      // Who wrote the review, and about whom. On the Trainer tab the student is
      // the author reviewing the trainer; on the Student tab it's reversed.
      reviewerName: r.reviewer,
      reviewerRole: isTrainer ? t('reviewPage.roleStudent') : t('reviewPage.roleTrainer'),
      revieweeName: g.name,
      revieweeRole: isTrainer ? t('reviewPage.roleTrainer') : t('reviewPage.roleStudent'),
    }))
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('reviewPage.title')}
        subtitle={isTrainer ? t('reviewPage.subtitleTrainer') : t('reviewPage.subtitleStudent')}
        breadcrumbs={[{label:t('reviewPage.breadcrumbDashboard'),to:isSchool?'/school/overview':'/dashboard'},{label:t('reviewPage.title')}]}
      />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex border-b border-gray-100 bg-white rounded-t-xl px-1 flex-1 min-w-64">
          {tabs.map(tb => <NavLink key={tb.path} to={tb.path}
            className={({isActive})=>`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${isActive?'border-brand-500 text-brand-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t(`reviewPage.${tb.labelKey}`)}
          </NavLink>)}
        </div>
        {!q.isLoading && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
            <MessageSquareText size={13} className="text-brand-500"/>
            <span className="font-bold text-gray-900">{t('reviewPage.reviewCount', {count: totalReviews})}</span>
          </div>
        )}
      </div>

      {q.isLoading ? (
        <div className="space-y-4">
          {Array.from({length:3}).map((_,i)=><div key={i} className="h-56 bg-gray-100 rounded-2xl animate-pulse"/>)}
        </div>
      ) : q.isError ? (
        <p className="text-sm text-red-500 px-1">{t('reviewPage.failedToLoad')}</p>
      ) : groupedReviews.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <MessageSquareText size={24} className="text-gray-300"/>
          </div>
          <p className="text-gray-400 text-sm font-semibold">{t('reviewPage.noReviews')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[860px]">
            <thead>
              <tr className="border-b border-gray-100 text-start text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                <th className="px-5 py-3.5 font-semibold">{t('reviewPage.columnReviewer')}</th>
                <th className="px-1 py-3.5"/>
                <th className="px-5 py-3.5 font-semibold">{t('reviewPage.columnReviewed')}</th>
                <th className="px-5 py-3.5 font-semibold whitespace-nowrap">{t('reviewPage.columnRating')}</th>
                <th className="px-5 py-3.5 font-semibold">{t('reviewPage.columnReview')}</th>
                <th className="px-5 py-3.5 font-semibold whitespace-nowrap">{t('reviewPage.columnDate')}</th>
                {!isSchool && <th className="px-5 py-3.5 font-semibold text-end">{t('reviewPage.columnAction')}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, i: number) => (
                <tr key={r.id ?? i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40 transition-colors align-middle">
                  {/* Reviewer — the person who left the review */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar name={r.reviewerName} size="sm"/>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate leading-tight">{r.reviewerName}</div>
                        <div className="text-[11px] text-gray-400 mt-0.5">{r.reviewerRole}</div>
                      </div>
                    </div>
                  </td>
                  {/* Direction */}
                  <td className="px-1 py-4 text-center">
                    <ArrowRight size={15} className="text-gray-300 inline-block rtl:rotate-180"/>
                  </td>
                  {/* Reviewed — the person the review is about */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar name={r.revieweeName} size="sm"/>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate leading-tight">{r.revieweeName}</div>
                        <div className="text-[11px] text-gray-400 mt-0.5">{r.revieweeRole}</div>
                      </div>
                    </div>
                  </td>
                  {/* Rating */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <Stars n={r.rating}/>
                      <span className={cn('text-xs font-bold', r.rating >= 4 ? 'text-green-600' : r.rating >= 3 ? 'text-brand-600' : 'text-red-500')}>
                        {r.rating.toFixed(1)}
                      </span>
                    </div>
                  </td>
                  {/* Review comment */}
                  <td className="px-5 py-4 max-w-sm">
                    {r.comment ? (
                      <p className="text-[13px] text-gray-600 leading-relaxed line-clamp-2" title={r.comment}>{r.comment}</p>
                    ) : (
                      <span className="text-[13px] italic text-gray-300">{t('reviewPage.noComment')}</span>
                    )}
                  </td>
                  {/* Date */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
                      <Calendar size={12} className="text-gray-300"/>{r.date ? formatDate(r.date) : '—'}
                    </div>
                  </td>
                  {/* Action */}
                  {!isSchool && (
                    <td className="px-5 py-4 text-end">
                      <Can perm="DELETE /admin/reviews/*">
                      <button onClick={()=>handleRemove(r.sessionId)} disabled={removingId===r.sessionId}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors" title={t('reviewPage.removeReviewTitle')}>
                        <Trash2 size={14}/>
                      </button>
                      </Can>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
